import classNames from 'classnames';
import React, { useCallback, useId, useMemo, useRef } from 'react';

import { AttentionMessage } from '@eika/attention-message';
import { FocusContainer } from '@eika/focus-container';
import { Icon, IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { Format } from '@/domain/app';

import useSelect from './useSelect';

import './CardSelect.scss';

type Props = {
    label: string;
    options: { title: string; cardNumber: string; value: string }[];
    onChange: (value: string) => void;
    selected: { value: string; title: string; cardNumber: string | undefined } | undefined;
    disabled?: boolean;
    placeholder?: string;
    isLoading?: boolean;
    emptyMessage?: string;
};

const CardSelect: React.FC<Props> = ({
    label,
    options,
    onChange,
    selected,
    disabled = false,
    placeholder,
    isLoading = false,
    emptyMessage,
}) => {
    const id = useId();
    const selectBoxRef = useRef<HTMLLabelElement | null>(null);
    const selectListRef = useRef<HTMLUListElement | null>(null);
    const selectRef = useRef<HTMLButtonElement | null>(null);

    const {
        onKeyDownSelect,
        onKeyDownSelectItem,
        closeSelect,
        show: open,
        setShow: setOpen,
    } = useSelect(selectListRef, selectBoxRef as React.RefObject<HTMLDivElement>, selectRef);

    const listItemKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>, item: string) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                onChange(item);
            }
            if (e.key === 'Esc' || e.key === 'Escape') {
                if (open) {
                    e.stopPropagation();
                }
                closeSelect();
                return;
            }

            onKeyDownSelectItem(e);
        },
        [open, onKeyDownSelectItem, closeSelect, onChange],
    );

    const selectKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Esc' || e.key === 'Escape') {
                if (open) {
                    e.stopPropagation();
                }
                closeSelect();
                return;
            }
            onKeyDownSelect(e);
        },
        [onKeyDownSelect, open, closeSelect],
    );

    const itemsWithoutSelected = useMemo(
        () => (selected?.value ? options.filter((x) => x.value !== selected.value) : options),
        [selected, options],
    );

    return (
        <section className="card-select__wrapper" ref={selectBoxRef}>
            <span id="label">{label}</span>
            <label
                htmlFor={id}
                className={classNames('card-select', { active: open, locked: disabled })}
                data-test-id="card-select"
            >
                <FocusContainer entry={open} onExit={() => setOpen(false)}>
                    <button
                        id={id}
                        aria-haspopup="listbox"
                        aria-expanded={open}
                        className={classNames('card-select__element', { active: open })}
                        onKeyDown={selectKeyDown}
                        disabled={disabled}
                        type="button"
                        ref={selectRef}
                        onClick={() => setOpen(!open)}
                        data-testid="apple-pay-card-select"
                    >
                        <span className={classNames('select__left-text', { selected })}>
                            {selected?.title ? selected.title : placeholder}
                        </span>
                    </button>
                    <span
                        className={classNames('card-select__left-sub-text', {
                            empty: selected?.cardNumber?.length === 0,
                        })}
                        data-testid="card-number-selected"
                    >
                        {selected?.cardNumber ? Format.cardNumber(selected.cardNumber) : ''}
                    </span>
                    <span className="card-select__icon">
                        {disabled ? <Icon type={IconTypes.PIN_KODE_24} /> : <Icon type={IconTypes.LITEN_PIL_NED} />}
                    </span>
                    <div className={classNames('card-select__dropdown', { active: open })}>
                        <ul
                            className="card-select__list"
                            aria-labelledby="hidden-label"
                            role="listbox"
                            aria-activedescendant={selected ? selected.value : undefined}
                            tabIndex={-1}
                            ref={selectListRef}
                        >
                            {isLoading && <Loader mini />}
                            {!isLoading &&
                                itemsWithoutSelected.length > 0 &&
                                itemsWithoutSelected.map((item) => (
                                    <li
                                        key={item.value}
                                        id={item.value}
                                        role="option"
                                        className="card-select__list-item"
                                        aria-selected={item.value === selected?.value}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => onChange(item.value)}
                                            className="card-select__list-item-button"
                                            onKeyDown={(e) => listItemKeyDown(e, item.value)}
                                        >
                                            <span className="card-select__list-item-left-text">{item.title}</span>
                                            <span
                                                className="card-select__list-item-left-sub-text"
                                                data-testid="card-select-number"
                                            >
                                                {Format.cardNumber(item.cardNumber)}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            {!isLoading && itemsWithoutSelected.length === 0 && selected && (
                                <li
                                    className="card-select__list-item is-empty"
                                    data-test-id="card-select-no-more-cards"
                                >
                                    <div className="card-select__list-item-button">
                                        <AttentionMessage
                                            className="card-select__list-item-left-text empty-text"
                                            type="info"
                                            text={emptyMessage}
                                            icon={IconTypes.UTROPSTEGN_24}
                                        />
                                        <span className="card-select__list-item-left-sub-text" />
                                    </div>
                                </li>
                            )}
                        </ul>
                    </div>
                </FocusContainer>
            </label>
        </section>
    );
};

export default CardSelect;
